import { Injectable, inject } from "@angular/core";
import { Store } from "@ngxs/store";
import { Observable, from, of } from "rxjs";
import { catchError, map, take } from "rxjs/operators";
import {
  COMPLETED_QUEST_ROUTE,
  COURSE_MENU_ITEM_ROUTE,
  COURSE_SESSIONS_PATH,
  EMERGENCY_HELP_PATH,
  HOME_PATH,
  LIFE_PRACTICE_ROUTE,
  MY_JOURNALS_PATH,
  QUEST_PATH,
  SHELL_QUEST_PAGE,
  UPDATE_PROFILE,
  WEBINAR_PATH,
} from "@sdk/lib/values/shared-paths";
import { Group } from "@sdk/lib/types/group";
import { Course, SessionModuleItem } from "@sdk/lib/types/course";
import { QuestState } from "../../states/quest.state";
import { GroupState } from "../../states/group-state";
import {
  buildModuleLocationIndex,
  formatModuleLocation,
} from "./module-location";

export type PageType =
  | "quest"
  | "webinar"
  | "module-shell"
  | "life-practice"
  | "update-profile"
  | "completed-quests"
  | "home"
  | "course-sessions"
  | "my-journey"
  | "emergency-help"
  | "course-menu-item"
  | "unknown";

export interface PageEntityContext {
  id?: string;
  title?: string;
  type?: string;
  readableId?: string;
  shortDescription?: string;
  longDescription?: string;
  instructions?: string;
  status?: "completed" | "in-progress" | "todo";
  location?: string;
}

export interface PageContext {
  pageType: PageType;
  label: string;
  description: string;
  entity?: PageEntityContext;
}

interface RouteRule {
  pageType: PageType;
  prefix: string;
  label: string;
  docFile: string;
}

const ROUTE_RULES: ReadonlyArray<RouteRule> = [
  {
    pageType: "quest",
    prefix: `/${QUEST_PATH}`,
    label: "Module",
    docFile: "quest.md",
  },
  {
    pageType: "webinar",
    prefix: `/${WEBINAR_PATH}`,
    label: "Webinar",
    docFile: "webinar.md",
  },
  {
    pageType: "module-shell",
    prefix: `/${SHELL_QUEST_PAGE}`,
    label: "Skill practice",
    docFile: "module-shell.md",
  },
  {
    pageType: "life-practice",
    prefix: `/${LIFE_PRACTICE_ROUTE}`,
    label: "Life practice",
    docFile: "life-practice.md",
  },
  {
    pageType: "update-profile",
    prefix: `/${UPDATE_PROFILE}`,
    label: "Update profile",
    docFile: "update-profile.md",
  },
  {
    pageType: "completed-quests",
    prefix: `/${COMPLETED_QUEST_ROUTE}`,
    label: "Completed modules",
    docFile: "completed-quests.md",
  },
  {
    pageType: "course-sessions",
    prefix: `/${COURSE_SESSIONS_PATH}`,
    label: "Course sessions",
    docFile: "course-sessions.md",
  },
  {
    pageType: "my-journey",
    prefix: `/${MY_JOURNALS_PATH}`,
    label: "My journey",
    docFile: "my-journey.md",
  },
  {
    pageType: "emergency-help",
    prefix: `/${EMERGENCY_HELP_PATH}`,
    label: "Emergency help",
    docFile: "emergency-help.md",
  },
  {
    pageType: "course-menu-item",
    prefix: `/${COURSE_MENU_ITEM_ROUTE}`,
    label: "Course menu",
    docFile: "course-menu-item.md",
  },
  {
    pageType: "home",
    prefix: `/${HOME_PATH}`,
    label: "Home",
    docFile: "home.md",
  },
];

const UNKNOWN_RULE: RouteRule = {
  pageType: "unknown",
  prefix: "",
  label: "Unknown page",
  docFile: "unknown.md",
};

const DOC_BASE_PATH = "/assets/page-docs";
const FALLBACK_DESCRIPTION = "No description available for this page.";

@Injectable({ providedIn: "root" })
export class PageContextService {
  private _store = inject(Store);
  private _docCache = new Map<string, Promise<string>>();

  resolveCurrentPage(url: string): Observable<PageContext> {
    const path = url.split(/[?#]/)[0];
    const rule = this._matchRule(path);
    const resolved = this._resolveEntity(rule.pageType, path);
    const entity = this._withLocation(resolved);

    return from(this._loadDoc(rule.docFile)).pipe(
      map((description) => ({
        pageType: rule.pageType,
        label: rule.label,
        description,
        ...(entity ? { entity } : {}),
      })),
      catchError(() =>
        of({
          pageType: rule.pageType,
          label: rule.label,
          description: FALLBACK_DESCRIPTION,
          ...(entity ? { entity } : {}),
        } satisfies PageContext),
      ),
      take(1),
    );
  }

  private _matchRule(path: string): RouteRule {
    for (const rule of ROUTE_RULES) {
      if (path === rule.prefix || path.startsWith(rule.prefix + "/")) {
        return rule;
      }
    }
    return UNKNOWN_RULE;
  }

  private async _loadDoc(filename: string): Promise<string> {
    let cached = this._docCache.get(filename);
    if (!cached) {
      cached = fetch(`${DOC_BASE_PATH}/${filename}`).then((res) => {
        if (!res.ok)
          throw new Error(`Failed to load ${filename}: ${res.status}`);
        return res.text();
      });
      this._docCache.set(filename, cached);
    }
    try {
      return await cached;
    } catch (err) {
      this._docCache.delete(filename);
      throw err;
    }
  }

  private _resolveEntity(
    pageType: PageType,
    path: string,
  ): PageEntityContext | undefined {
    switch (pageType) {
      case "quest":
        return this._resolveQuestEntity();
      case "webinar":
        return this._resolveWebinarEntity(path);
      case "module-shell":
        return this._resolveModuleShellEntity(path);
      case "life-practice":
        return this._resolveLifePracticeEntity(path);
      default:
        return undefined;
    }
  }

  private _withLocation(
    entity: PageEntityContext | undefined,
  ): PageEntityContext | undefined {
    if (!entity?.id) return entity;
    const group = this._store.selectSnapshot<Group | null>(GroupState as any);
    const course = group?.course as Course | undefined;
    if (!course) return entity;
    const location = buildModuleLocationIndex(course).get(entity.id);
    if (!location) return entity;
    return { ...entity, location: formatModuleLocation(location) };
  }

  private _resolveQuestEntity(): PageEntityContext | undefined {
    const quest = this._store.selectSnapshot(QuestState.questBeingEdited);
    if (!quest) return undefined;
    return {
      id: quest._id?.toString(),
      title: quest.title,
      readableId: quest.readable_id,
      shortDescription: quest.short_description,
      longDescription: quest.long_description,
      status: quest.is_complete
        ? "completed"
        : quest.last_viewed_block
          ? "in-progress"
          : "todo",
      type: "quest",
    };
  }

  private _resolveWebinarEntity(path: string): PageEntityContext | undefined {
    const webinarId = this._extractSegmentAfter(path, `/${WEBINAR_PATH}`);
    if (!webinarId) return undefined;
    const item = this._findModuleItemBy(
      (moduleItem) => moduleItem._id?.toString() === webinarId,
    );
    if (!item) return { id: webinarId, type: "webinar" };
    const moduleItem = item.module_item as any;
    return {
      id: webinarId,
      title: moduleItem?.title || item.module_shell?.title,
      readableId: moduleItem?.readable_id,
      shortDescription: moduleItem?.short_description,
      longDescription: moduleItem?.long_description,
      type: "webinar",
    };
  }

  private _resolveModuleShellEntity(
    path: string,
  ): PageEntityContext | undefined {
    const shellId = this._extractSegmentAfter(path, `/${SHELL_QUEST_PAGE}`);
    if (!shellId) return undefined;
    const item = this._findModuleItemBy(
      (_moduleItem, sessionItem) =>
        sessionItem.module_shell?._id?.toString() === shellId,
    );
    if (!item) return { id: shellId, type: "module-shell" };
    const moduleItem = item.module_item as any;
    const skillName = (item.module_shell as any)?.course_theme_config
      ?.skill_header as string | undefined;
    const shortDescription = [
      skillName?.trim() ? `Skill practice: ${skillName.trim()}` : null,
      moduleItem?.short_description,
    ]
      .filter(Boolean)
      .join(" — ");
    return {
      id: shellId,
      title: item.module_shell?.title || moduleItem?.title,
      shortDescription: shortDescription || undefined,
      longDescription: moduleItem?.long_description,
      type: "module-shell",
    };
  }

  private _resolveLifePracticeEntity(
    path: string,
  ): PageEntityContext | undefined {
    // The life-practice URL embeds the module-item id among other segments
    // (group/course/session ids plus a trailing view name like
    // "list-life-practice"), so match whichever segment is a known module
    // item rather than assuming a fixed position.
    const segments = path.split("/").filter(Boolean);
    let item: SessionModuleItem | undefined;
    let moduleItemId: string | undefined;
    for (const segment of segments) {
      const found = this._findModuleItemBy(
        (moduleItem) => moduleItem._id?.toString() === segment,
      );
      if (found) {
        item = found;
        moduleItemId = segment;
        break;
      }
    }
    if (!moduleItemId) return undefined;
    if (!item) return { id: moduleItemId, type: "life-practice" };
    const moduleItem = item.module_item as any;
    return {
      id: moduleItemId,
      title: moduleItem?.title,
      readableId: moduleItem?.readable_id,
      shortDescription: moduleItem?.short_description,
      longDescription: moduleItem?.long_description,
      type: "life-practice",
    };
  }

  private _extractSegmentAfter(path: string, prefix: string): string | null {
    if (!path.startsWith(prefix + "/")) return null;
    const remainder = path.slice(prefix.length + 1);
    const next = remainder.split("/")[0];
    return next || null;
  }

  private _findModuleItemBy(
    predicate: (
      moduleItem: { _id?: any },
      sessionItem: SessionModuleItem,
    ) => boolean,
  ): SessionModuleItem | undefined {
    const group = this._store.selectSnapshot<Group | null>(GroupState as any);
    const course = group?.course as Course | undefined;
    if (!course) return undefined;
    const sessions = [
      ...(course.session_list ?? []),
      ...(course.course_menu_item_list ?? []).flatMap(
        (mi) => mi.menu_item_session_list ?? [],
      ),
    ];
    for (const session of sessions) {
      for (const item of session.session_module_item_list ?? []) {
        const moduleItem = item.module_item as { _id?: any } | undefined;
        if (!moduleItem) continue;
        if (predicate(moduleItem, item)) return item;
      }
    }
    return undefined;
  }
}
